import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from './Modal';
import { ApplicationStatus } from '../../types/drivers';

interface ChangeDriverStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (status: ApplicationStatus, blockReason: string | null) => void;
  currentStatus: ApplicationStatus;
  loading?: boolean;
}

const ChangeDriverStatusModal: React.FC<ChangeDriverStatusModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  currentStatus,
  loading = false
}) => {
  const { t } = useTranslation();
  const [selectedStatus, setSelectedStatus] = useState<ApplicationStatus>(currentStatus);
  const [blockReason, setBlockReason] = useState('');

  useEffect(() => {
    if (isOpen) {
      setSelectedStatus(currentStatus);
      setBlockReason('');
    }
  }, [isOpen, currentStatus]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedStatus === ApplicationStatus.BLOCKED && !blockReason.trim()) {
      return;
    }

    onSubmit(
      selectedStatus,
      selectedStatus === ApplicationStatus.BLOCKED ? blockReason.trim() : null
    );
  };

  const handleClose = () => {
    setSelectedStatus(currentStatus);
    setBlockReason('');
    onClose();
  };

  const isFormValid = () => {
    if (selectedStatus === currentStatus) {
      return false;
    }
    if (selectedStatus === ApplicationStatus.BLOCKED && !blockReason.trim()) {
      return false;
    }
    return true;
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={t('modals.changeDriverStatus.title')}>
      <form onSubmit={handleSubmit} className="modal-form">
        <div className="modal-form-group">
          <label htmlFor="status">{t('modals.changeDriverStatus.selectStatus')}</label>
          <select
            id="status"
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value as ApplicationStatus)}
            required
            disabled={loading}
          >
            <option value={ApplicationStatus.PENDING}>{t('modals.changeDriverStatus.pending')}</option>
            <option value={ApplicationStatus.ACCEPTED}>{t('modals.changeDriverStatus.accepted')}</option>
            <option value={ApplicationStatus.BLOCKED}>{t('modals.changeDriverStatus.blocked')}</option>
          </select>
        </div>

        {selectedStatus === ApplicationStatus.BLOCKED && (
          <div className="modal-form-group">
            <label htmlFor="blockReason">{t('modals.changeDriverStatus.blockReasonLabel')}</label>
            <textarea
              id="blockReason"
              value={blockReason}
              onChange={(e) => setBlockReason(e.target.value)}
              placeholder={t('modals.changeDriverStatus.blockReasonPlaceholder')}
              required
              disabled={loading}
              rows={4}
            />
          </div>
        )}

        <div className="modal-actions">
          <button
            type="button"
            onClick={handleClose}
            className="modal-button secondary"
            disabled={loading}
          >
            {t('modals.changeDriverStatus.cancel')}
          </button>
          <button
            type="submit"
            className="modal-button primary"
            disabled={loading || !isFormValid()}
          >
            {loading ? t('modals.changeDriverStatus.changing') : t('modals.changeDriverStatus.change')}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default ChangeDriverStatusModal;
